import React, { useState, useEffect } from "react";
import {
  Building2,
  ShieldCheck,
  CheckCircle2,
  AlertTriangle,
  ArrowRight,
  ArrowLeft,
  Key,
  Mail,
  Sliders,
  Layers,
  Sparkles,
  Lock,
  Printer,
  Zap,
  RefreshCw,
  Eye,
  EyeOff,
  Check,
  X,
  Server,
  Database,
  Cpu,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  OtpVerificationService,
  OTP_EXPIRY_MINUTES,
  RESEND_COOLDOWN_SECONDS,
} from "@/lib/security/otp-verification-service";
import {
  INITIAL_JELLY_MODULES,
  type JellyModule,
  saveJellyAdminState,
} from "@/lib/administration/jelly-module-definitions";
import { useInstallationConfigStore } from "@/lib/installation-config";

export interface SetupWizardProps {
  onComplete?: () => void;
}

type SetupMode = "select" | "already_configured" | "custom_wizard";

export const SetupWizard: React.FC<SetupWizardProps> = ({ onComplete }) => {
  const [mode, setMode] = useState<SetupMode>("select");

  // ── Quick Mode State ───────────────────────────────────────────────────────
  const [quickCompanyName, setQuickCompanyName] = useState("AVS Gold Enterprise");
  const [quickCompanyCode, setQuickCompanyCode] = useState("AVS-01");
  const [quickPassword, setQuickPassword] = useState("master123");
  const [quickConfirm, setQuickConfirm] = useState("master123");
  const [quickError, setQuickError] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);

  // ── Custom Wizard State ───────────────────────────────────────────────────
  const [currentStep, setCurrentStep] = useState(0);

  // Step 1: Mandatory Email OTP Verification
  const [ownerEmail, setOwnerEmail] = useState("aritramanna222@gmail.com");
  const [otpInput, setOtpInput] = useState("");
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [otpStatusMsg, setOtpStatusMsg] = useState("");
  const [debugOtp, setDebugOtp] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);

  // Step 2: Company Profile
  const [compName, setCompName] = useState("Arivahly Gold Refinery & Works");
  const [compCode, setCompCode] = useState("AGRW");
  const [compAddress, setCompAddress] = useState("12 Jewellery Lane, Zaveri Bazaar, Mumbai 400002");
  const [compPhone, setCompPhone] = useState("+91 98765 43210");
  const [compGstin, setCompGstin] = useState("27AADCA1234A1Z5");
  const [compPan, setCompPan] = useState("AADCA1234A");

  // Step 3: Pure Gold Benchmark & Calculation Rules
  const [benchmark, setBenchmark] = useState(995);
  const [gstRate, setGstRate] = useState(3.0);
  const [wastageTolerance, setWastageTolerance] = useState(1.0);

  // Step 4: Jelly-Style Modular Architecture
  const [modules, setModules] = useState<JellyModule[]>(INITIAL_JELLY_MODULES);

  // Step 5: Communication (Hostinger SMTP & WhatsApp API)
  const [smtpHost, setSmtpHost] = useState("smtp.hostinger.com");
  const [smtpPort, setSmtpPort] = useState(465);
  const [smtpUser, setSmtpUser] = useState("aritra@erp.arivahly.in");
  const [smtpPass, setSmtpPass] = useState("Aritramanna@222");
  const [enableWhatsApp, setEnableWhatsApp] = useState(false);
  const [waPhoneId, setWaPhoneId] = useState("");
  const [waToken, setWaToken] = useState("");

  // Step 6: AI & Automations
  const [enableOllama, setEnableOllama] = useState(true);
  const [ollamaModel, setOllamaModel] = useState("llama3.2");
  const [enableDailyClose, setEnableDailyClose] = useState(true);
  const [dailyCloseTime, setDailyCloseTime] = useState("21:00");
  const [enableOwnerDigest, setEnableOwnerDigest] = useState(true);

  // Step 7: Master Password & Security
  const [custPassword, setCustPassword] = useState("");
  const [custConfirm, setCustConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [customError, setCustomError] = useState("");

  // Cooldown countdown timer
  useEffect(() => {
    if (cooldown <= 0) return;
    const interval = setInterval(() => {
      setCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(interval);
  }, [cooldown]);

  // Handle Send OTP
  const handleSendOtp = async () => {
    setOtpStatusMsg("Dispatching cryptographic OTP via Hostinger SMTP...");
    const res = await OtpVerificationService.sendOtp(ownerEmail, "Setup Verification");
    if (res.success) {
      setIsOtpSent(true);
      setOtpStatusMsg(res.message);
      if (res.debugOtp) setDebugOtp(res.debugOtp);
      if (res.cooldownSeconds) setCooldown(res.cooldownSeconds);
    } else {
      setOtpStatusMsg(res.message);
    }
  };

  // Handle Verify OTP
  const handleVerifyOtp = async () => {
    if (otpInput.trim().length !== 6) {
      setOtpStatusMsg("Please enter all 6 digits of the verification code.");
      return;
    }
    const res = await OtpVerificationService.validateOtp(otpInput, ownerEmail);
    if (res.valid) {
      setIsEmailVerified(true);
      setOtpStatusMsg("✓ Owner email verified successfully! You may now proceed.");
    } else {
      setOtpStatusMsg(res.message);
    }
  };

  // Toggle module in Jelly tree
  const toggleModule = (modId: string) => {
    setModules((prev) =>
      prev.map((m) => {
        if (m.id === modId) {
          if (m.isCore) return m; // core modules cannot be disabled
          return { ...m, enabled: !m.enabled };
        }
        return m;
      })
    );
  };

  // Toggle sub-feature in module
  const toggleFeature = (modId: string, featId: string) => {
    setModules((prev) =>
      prev.map((m) => {
        if (m.id === modId) {
          return {
            ...m,
            features: m.features.map((f) => (f.id === featId ? { ...f, enabled: !f.enabled } : f)),
          };
        }
        return m;
      })
    );
  };

  // Finish Already Configured Setup
  const handleFinishAlreadyConfigured = async () => {
    setQuickError("");
    if (!quickCompanyName.trim()) {
      setQuickError("Company Name is required.");
      return;
    }
    if (!quickPassword || quickPassword.length < 6) {
      setQuickError("Master password must be at least 6 characters.");
      return;
    }
    if (quickPassword !== quickConfirm) {
      setQuickError("Passwords do not match.");
      return;
    }

    setIsProcessing(true);
    try {
      // Save state to Jelly Administration state
      saveJellyAdminState({
        isLocked: true,
        lockTimestamp: new Date().toISOString(),
        activeModules: INITIAL_JELLY_MODULES,
      });

      // Update installation config store
      const configStore = useInstallationConfigStore.getState();
      configStore.setBusinessProfile({
        shopName: quickCompanyName.trim(),
        legalName: quickCompanyName.trim(),
        tradeName: quickCompanyName.trim(),
      });
      configStore.markSetupCompleted(true);
      await configStore.saveInstallationConfig();

      // Native host interop if available
      try {
        const native = (window as any).chrome?.webview?.hostObjects?.avsNative;
        if (native && native.CreateCompany) {
          await native.CreateCompany(quickCompanyName.trim(), quickCompanyCode.trim() || "AVS-01", quickPassword);
        }
      } catch (e) {
        // web environment
      }

      localStorage.setItem("avs_setup_completed", "true");
      localStorage.setItem("avs_configuration_mode", "already_configured");

      if (onComplete) {
        onComplete();
      } else {
        window.location.href = "/app";
      }
    } catch (err: any) {
      setQuickError(err.message || "Failed to initialize company.");
      setIsProcessing(false);
    }
  };

  // Finish Custom Setup
  const handleFinishCustomSetup = async () => {
    setCustomError("");
    if (!custPassword || custPassword.length < 6) {
      setCustomError("Master password must be at least 6 characters.");
      return;
    }
    if (custPassword !== custConfirm) {
      setCustomError("Passwords do not match.");
      return;
    }

    setIsProcessing(true);
    try {
      // Save custom modular definitions and configuration lock
      saveJellyAdminState({
        isLocked: true,
        lockTimestamp: new Date().toISOString(),
        activeModules: modules,
      });

      // Update installation config store
      const configStore = useInstallationConfigStore.getState();
      configStore.setBusinessProfile({
        shopName: compName.trim(),
        legalName: compName.trim(),
        tradeName: compName.trim(),
        address: compAddress,
        phone: compPhone,
        gstin: compGstin,
        pan: compPan,
        email: ownerEmail,
      });
      configStore.markSetupCompleted(true);
      await configStore.saveInstallationConfig();

      // Save locked configuration snapshot to localStorage
      localStorage.setItem("avs_setup_completed", "true");
      localStorage.setItem("avs_configuration_mode", "custom_wizard");
      localStorage.setItem(
        "avs_configuration_state",
        JSON.stringify({
          state: "LOCKED_CONFIGURATION",
          verifiedOwnerEmail: ownerEmail,
          benchmark,
          gstRate,
          wastageTolerance,
          smtp: { host: smtpHost, port: smtpPort, user: smtpUser },
          whatsappEnabled: enableWhatsApp,
          aiOllamaEnabled: enableOllama,
          lockedAt: Date.now(),
        })
      );

      // Native host interop if available
      try {
        const native = (window as any).chrome?.webview?.hostObjects?.avsNative;
        if (native && native.CreateCompany) {
          await native.CreateCompany(compName.trim(), compCode.trim() || "AVS-01", custPassword);
        }
      } catch (e) {
        // web environment
      }

      if (onComplete) {
        onComplete();
      } else {
        window.location.href = "/app";
      }
    } catch (err: any) {
      setCustomError(err.message || "Failed to finalize setup.");
      setIsProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col items-center justify-center p-4 sm:p-6 font-sans select-none">
      {/* Top Branding Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="h-11 w-11 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-500 shadow-lg shadow-amber-500/10">
          <Building2 className="h-6 w-6" />
        </div>
        <div>
          <h1 className="text-xl font-bold tracking-tight text-white flex items-center gap-2">
            AVS GOLD ERP
            <Badge variant="outline" className="text-amber-400 border-amber-500/30 bg-amber-500/10 text-xs px-2 py-0.5">
              995 Fine Benchmark
            </Badge>
          </h1>
          <p className="text-xs text-slate-400">Jewellery Enterprise Architecture · Complete Setup & Modular Administration</p>
        </div>
      </div>

      {/* ── 1. MODE SELECTION SCREEN ────────────────────────────────────────── */}
      {mode === "select" && (
        <div className="w-full max-w-3xl bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl overflow-hidden p-6 sm:p-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-white mb-2">Select Installation Setup Mode</h2>
            <p className="text-sm text-slate-400 max-w-lg mx-auto">
              Choose how you wish to configure your AVS Gold ERP workspace. Both modes provide the identical, complete ERP engine.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {/* Mode A Card: ALREADY CONFIGURED */}
            <div
              onClick={() => setMode("already_configured")}
              className="relative group cursor-pointer bg-slate-800/60 hover:bg-slate-800 border border-slate-700 hover:border-amber-500/60 rounded-xl p-6 transition-all duration-200 hover:shadow-xl hover:shadow-amber-500/5 flex flex-col justify-between"
            >
              <div className="absolute top-4 right-4">
                <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 text-[11px]">Recommended</Badge>
              </div>
              <div>
                <div className="h-10 w-10 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-500 mb-4">
                  <Zap className="h-5 w-5" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2 group-hover:text-amber-400 transition-colors">
                  ALREADY CONFIGURED
                </h3>
                <p className="text-xs text-slate-300 leading-relaxed mb-4">
                  Load the predefined authoritative AVS configuration and start the ERP immediately with minimal setup.
                </p>
                <div className="space-y-1.5 text-xs text-slate-400">
                  <div className="flex items-center gap-2">
                    <Check className="h-3.5 w-3.5 text-amber-500" />
                    <span>Strict 995 Fine Gold Benchmark denominator</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="h-3.5 w-3.5 text-amber-500" />
                    <span>Standard GST 3% (CGST 1.5% + SGST 1.5%)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="h-3.5 w-3.5 text-amber-500" />
                    <span>Pre-configured 14 Enterprise Modules</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="h-3.5 w-3.5 text-amber-500" />
                    <span>Double-entry Rojmel & Karigar Khata</span>
                  </div>
                </div>
              </div>
              <Button className="mt-6 w-full bg-amber-600 hover:bg-amber-500 text-white font-medium text-xs h-9">
                Load Predefined Setup →
              </Button>
            </div>

            {/* Mode B Card: CUSTOM SETUP */}
            <div
              onClick={() => setMode("custom_wizard")}
              className="relative group cursor-pointer bg-slate-800/60 hover:bg-slate-800 border border-slate-700 hover:border-slate-500 rounded-xl p-6 transition-all duration-200 hover:shadow-xl flex flex-col justify-between"
            >
              <div>
                <div className="h-10 w-10 rounded-lg bg-slate-700/60 border border-slate-600 flex items-center justify-center text-slate-300 mb-4">
                  <Sliders className="h-5 w-5" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2 group-hover:text-slate-200 transition-colors">
                  CUSTOM SETUP
                </h3>
                <p className="text-xs text-slate-300 leading-relaxed mb-4">
                  Step-by-step custom wizard to tailor company identity, module selection, email, AI, and security rules.
                </p>
                <div className="space-y-1.5 text-xs text-slate-400">
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="h-3.5 w-3.5 text-slate-400" />
                    <span>Mandatory 6-digit cryptographic OTP verification</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Layers className="h-3.5 w-3.5 text-slate-400" />
                    <span>Jelly-style module toggles & sub-feature flags</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Server className="h-3.5 w-3.5 text-slate-400" />
                    <span>Hostinger SMTP & customer-owned WhatsApp</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Lock className="h-3.5 w-3.5 text-slate-400" />
                    <span>Final configuration lock against tampering</span>
                  </div>
                </div>
              </div>
              <Button variant="outline" className="mt-6 w-full border-slate-700 hover:bg-slate-700 text-slate-200 text-xs h-9">
                Custom Setup Wizard →
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ── 2. ALREADY CONFIGURED SCREEN ────────────────────────────────────── */}
      {mode === "already_configured" && (
        <div className="w-full max-w-xl bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl p-6 sm:p-8">
          <div className="flex items-center justify-between mb-6 border-b border-slate-800 pb-4">
            <div>
              <h2 className="text-xl font-bold text-white">Predefined AVS Configuration</h2>
              <p className="text-xs text-slate-400">Immediate start with authoritative 995 benchmark</p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setMode("select")}
              className="text-xs text-slate-400 hover:text-white"
            >
              ← Change Mode
            </Button>
          </div>

          {quickError && (
            <div className="mb-4 p-3 bg-red-950/50 border border-red-800/80 rounded-lg text-red-200 text-xs flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 shrink-0 text-red-400" />
              <span>{quickError}</span>
            </div>
          )}

          <div className="space-y-4 mb-6">
            <div>
              <label className="block text-xs font-semibold text-slate-300 mb-1.5">Company Legal Name</label>
              <Input
                value={quickCompanyName}
                onChange={(e) => setQuickCompanyName(e.target.value)}
                placeholder="e.g. Maa Tara Jewellers"
                className="bg-slate-800/80 border-slate-700 text-white text-sm"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-300 mb-1.5">Company Code</label>
              <Input
                value={quickCompanyCode}
                onChange={(e) => setQuickCompanyCode(e.target.value.toUpperCase())}
                placeholder="e.g. MTJ-01"
                className="bg-slate-800/80 border-slate-700 text-white text-sm uppercase font-mono"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1.5">Master Password</label>
                <Input
                  type="password"
                  value={quickPassword}
                  onChange={(e) => setQuickPassword(e.target.value)}
                  placeholder="••••••••"
                  className="bg-slate-800/80 border-slate-700 text-white text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1.5">Confirm Password</label>
                <Input
                  type="password"
                  value={quickConfirm}
                  onChange={(e) => setQuickConfirm(e.target.value)}
                  placeholder="••••••••"
                  className="bg-slate-800/80 border-slate-700 text-white text-sm"
                />
              </div>
            </div>

            <div className="p-3 bg-amber-500/5 border border-amber-500/20 rounded-lg">
              <div className="text-xs font-semibold text-amber-400 mb-1">Predefined Package Summary:</div>
              <p className="text-[11px] text-slate-400 leading-relaxed">
                • Standard Denominator: <strong>995 Fine Gold Benchmark</strong><br />
                • Financial Year: <strong>2026–2027</strong> | GST: <strong>3% Jewellery Standard</strong><br />
                • 14 Enterprise Modules active and configurable later from the Admin Panel.
              </p>
            </div>
          </div>

          <Button
            onClick={handleFinishAlreadyConfigured}
            disabled={isProcessing}
            className="w-full bg-amber-600 hover:bg-amber-500 text-white font-bold h-10 text-sm shadow-lg shadow-amber-600/20"
          >
            {isProcessing ? "Configuring & Starting..." : "Load Predefined Package & Start ERP"}
          </Button>
        </div>
      )}

      {/* ── 3. CUSTOM SETUP WIZARD ─────────────────────────────────────────── */}
      {mode === "custom_wizard" && (
        <div className="w-full max-w-3xl bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl p-6 sm:p-8">
          {/* Wizard Step Breadcrumb */}
          <div className="flex items-center justify-between border-b border-slate-800 pb-4 mb-6">
            <div className="flex items-center gap-2">
              <span className="text-xs font-mono text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded border border-amber-500/20">
                Step {currentStep + 1} of 7
              </span>
              <h2 className="text-lg font-bold text-white">
                {currentStep === 0 && "1. Mandatory Email OTP Verification"}
                {currentStep === 1 && "2. Company Identity & Profile"}
                {currentStep === 2 && "3. Pure Gold Benchmark & Calculation"}
                {currentStep === 3 && "4. Jelly-Style Modular Selection"}
                {currentStep === 4 && "5. Communication Setup (Hostinger SMTP)"}
                {currentStep === 5 && "6. AI & Operational Automations"}
                {currentStep === 6 && "7. Master Password & Configuration Lock"}
              </h2>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setMode("select")}
              className="text-xs text-slate-400 hover:text-white"
            >
              Exit Setup
            </Button>
          </div>

          {customError && (
            <div className="mb-4 p-3 bg-red-950/50 border border-red-800/80 rounded-lg text-red-200 text-xs flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 shrink-0 text-red-400" />
              <span>{customError}</span>
            </div>
          )}

          {/* STEP 0: MANDATORY 6-DIGIT CRYPTOGRAPHIC OTP VERIFICATION */}
          {currentStep === 0 && (
            <div className="space-y-6">
              <div className="p-4 bg-slate-800/50 border border-slate-700/80 rounded-xl">
                <p className="text-xs text-slate-300 leading-relaxed mb-3">
                  Before allowing installation to proceed, you must verify the Administrator / Owner email address using a cryptographic one-time password (OTP).
                </p>
                <div className="flex items-center gap-2 text-xs text-amber-400">
                  <ShieldCheck className="h-4 w-4" />
                  <span>Dispatched via Hostinger SMTP infrastructure directly to your inbox</span>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1.5">Owner / Administrator Email</label>
                <div className="flex gap-2">
                  <Input
                    type="email"
                    value={ownerEmail}
                    onChange={(e) => setOwnerEmail(e.target.value)}
                    disabled={isEmailVerified}
                    placeholder="owner@example.com"
                    className="bg-slate-800/80 border-slate-700 text-white text-sm"
                  />
                  <Button
                    onClick={handleSendOtp}
                    disabled={cooldown > 0 || isEmailVerified}
                    className="bg-amber-600 hover:bg-amber-500 text-white text-xs whitespace-nowrap px-4"
                  >
                    {cooldown > 0 ? `Resend in ${cooldown}s` : isOtpSent ? "Resend OTP" : "Send OTP"}
                  </Button>
                </div>
              </div>

              {isOtpSent && !isEmailVerified && (
                <div className="p-5 bg-slate-800/80 border border-amber-500/30 rounded-xl space-y-4">
                  <div>
                    <label className="block text-xs font-semibold text-amber-400 mb-1">
                      Enter 6-Digit Verification Code
                    </label>
                    <p className="text-[11px] text-slate-400 mb-2">
                      Code dispatched to <span className="text-white font-mono">{ownerEmail}</span>. Valid for {OTP_EXPIRY_MINUTES} minutes.
                    </p>
                    <div className="flex gap-2">
                      <Input
                        value={otpInput}
                        maxLength={6}
                        onChange={(e) => setOtpInput(e.target.value.replace(/\D/g, ""))}
                        placeholder="000000"
                        className="bg-slate-950 border-amber-500/50 text-amber-400 text-center text-xl font-mono tracking-widest max-w-[180px] h-11"
                      />
                      <Button
                        onClick={handleVerifyOtp}
                        className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold px-6 h-11"
                      >
                        Verify OTP
                      </Button>
                    </div>
                  </div>

                  {debugOtp && (
                    <div className="p-2.5 bg-slate-900 border border-slate-700 rounded text-[11px] text-slate-400">
                      <span className="text-slate-500">Development Debug Code:</span>{" "}
                      <span className="font-mono text-amber-400 font-bold">{debugOtp}</span>
                    </div>
                  )}
                </div>
              )}

              {isEmailVerified && (
                <div className="p-4 bg-emerald-950/40 border border-emerald-600/50 rounded-xl flex items-center gap-3 text-emerald-300 text-xs">
                  <CheckCircle2 className="h-5 w-5 text-emerald-400 shrink-0" />
                  <div>
                    <span className="font-bold">Email Verified:</span> {ownerEmail}
                    <p className="text-[11px] text-emerald-400/80">
                      Owner identity established. You may now continue the installation.
                    </p>
                  </div>
                </div>
              )}

              {otpStatusMsg && !isEmailVerified && (
                <p className="text-xs text-slate-400">{otpStatusMsg}</p>
              )}
            </div>
          )}

          {/* STEP 1: COMPANY IDENTITY & PROFILE */}
          {currentStep === 1 && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1">Company Legal Name</label>
                  <Input
                    value={compName}
                    onChange={(e) => setCompName(e.target.value)}
                    placeholder="e.g. Arivahly Gold Refinery"
                    className="bg-slate-800/80 border-slate-700 text-white text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1">Company Code</label>
                  <Input
                    value={compCode}
                    onChange={(e) => setCompCode(e.target.value.toUpperCase())}
                    placeholder="e.g. AGRW"
                    className="bg-slate-800/80 border-slate-700 text-white text-sm uppercase font-mono"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1">Registered Address</label>
                <Input
                  value={compAddress}
                  onChange={(e) => setCompAddress(e.target.value)}
                  placeholder="Street, City, Pin"
                  className="bg-slate-800/80 border-slate-700 text-white text-sm"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1">Phone Number</label>
                  <Input
                    value={compPhone}
                    onChange={(e) => setCompPhone(e.target.value)}
                    className="bg-slate-800/80 border-slate-700 text-white text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1">GSTIN</label>
                  <Input
                    value={compGstin}
                    onChange={(e) => setCompGstin(e.target.value.toUpperCase())}
                    className="bg-slate-800/80 border-slate-700 text-white text-sm uppercase font-mono"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1">PAN</label>
                  <Input
                    value={compPan}
                    onChange={(e) => setCompPan(e.target.value.toUpperCase())}
                    className="bg-slate-800/80 border-slate-700 text-white text-sm uppercase font-mono"
                  />
                </div>
              </div>
            </div>
          )}

          {/* STEP 2: PURE GOLD BENCHMARK */}
          {currentStep === 2 && (
            <div className="space-y-4">
              <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl">
                <h3 className="text-sm font-bold text-amber-400 mb-1">Authoritative 995 Fine Gold Benchmark</h3>
                <p className="text-xs text-slate-300 leading-relaxed">
                  In AVS ERP, <strong>995 is the firm denominator</strong>. Pure fine conversion is calculated as:
                  <code className="block mt-1.5 p-2 bg-slate-950 rounded text-amber-400 font-mono text-xs">
                    Fine mg @ 995 = Math.round((gross_mg * purity) / 995)
                  </code>
                  Actual assay (1000, 999, 995, 916, 750) is permanently preserved without rounding or loss.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1">Firm Benchmark Standard</label>
                  <Input
                    type="number"
                    value={benchmark}
                    disabled
                    className="bg-slate-800/80 border-slate-700 text-amber-400 font-bold font-mono"
                  />
                  <span className="text-[10px] text-slate-500">Locked to 995 benchmark standard</span>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1">Jewellery GST Rate (%)</label>
                  <Input
                    type="number"
                    step="0.1"
                    value={gstRate}
                    onChange={(e) => setGstRate(parseFloat(e.target.value) || 3.0)}
                    className="bg-slate-800/80 border-slate-700 text-white font-mono"
                  />
                  <span className="text-[10px] text-slate-500">CGST 1.5% + SGST 1.5%</span>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1">Wastage Tolerance (%)</label>
                  <Input
                    type="number"
                    step="0.1"
                    value={wastageTolerance}
                    onChange={(e) => setWastageTolerance(parseFloat(e.target.value) || 1.0)}
                    className="bg-slate-800/80 border-slate-700 text-white font-mono"
                  />
                  <span className="text-[10px] text-slate-500">Max allowable Karigar loss</span>
                </div>
              </div>
            </div>
          )}

          {/* STEP 3: JELLY-STYLE MODULAR ARCHITECTURE (14 MODULES) */}
          {currentStep === 3 && (
            <div className="space-y-4 max-h-[420px] overflow-y-auto pr-2">
              <p className="text-xs text-slate-400">
                Configure module enablement. Disabling a module hides its navigation and tools while safely preserving all underlying data.
              </p>

              <div className="space-y-2">
                {modules.map((m) => (
                  <div
                    key={m.id}
                    className={`p-3.5 rounded-xl border transition-all ${
                      m.enabled ? "bg-slate-800/60 border-slate-700" : "bg-slate-900/40 border-slate-800 opacity-60"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Switch
                          checked={m.enabled}
                          disabled={m.isCore}
                          onCheckedChange={() => toggleModule(m.id)}
                        />
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold text-white">{m.name}</span>
                            <Badge variant="outline" className="text-[10px] py-0 px-1.5 border-slate-700 text-slate-400">
                              {m.category}
                            </Badge>
                            {m.isCore && (
                              <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/20 text-[10px] py-0">
                                Core
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-slate-400">{m.description}</p>
                        </div>
                      </div>
                    </div>

                    {m.enabled && m.features.length > 0 && (
                      <div className="mt-3 pt-2.5 border-t border-slate-700/50 pl-11 grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {m.features.map((f) => (
                          <label key={f.id} className="flex items-center gap-2 cursor-pointer text-xs text-slate-300">
                            <input
                              type="checkbox"
                              checked={f.enabled}
                              onChange={() => toggleFeature(m.id, f.id)}
                              className="rounded border-slate-700 bg-slate-900 text-amber-500 focus:ring-amber-500"
                            />
                            <span>{f.name}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* STEP 4: COMMUNICATION SETUP */}
          {currentStep === 4 && (
            <div className="space-y-5">
              <div className="p-4 bg-slate-800/60 border border-slate-700 rounded-xl">
                <h3 className="text-xs font-bold text-amber-400 mb-1 flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  Hostinger SMTP Email Engine
                </h3>
                <p className="text-[11px] text-slate-400 mb-4">
                  Direct dispatch of owner reports, daily digests, and security alerts through your Hostinger mail account.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-slate-300 mb-1">SMTP Host</label>
                    <Input
                      value={smtpHost}
                      onChange={(e) => setSmtpHost(e.target.value)}
                      className="bg-slate-900 border-slate-700 text-white text-xs font-mono"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-300 mb-1">SMTP Port</label>
                    <Input
                      type="number"
                      value={smtpPort}
                      onChange={(e) => setSmtpPort(parseInt(e.target.value) || 465)}
                      className="bg-slate-900 border-slate-700 text-white text-xs font-mono"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-300 mb-1">Username / Sender</label>
                    <Input
                      value={smtpUser}
                      onChange={(e) => setSmtpUser(e.target.value)}
                      className="bg-slate-900 border-slate-700 text-white text-xs"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-300 mb-1">Password</label>
                    <Input
                      type="password"
                      value={smtpPass}
                      onChange={(e) => setSmtpPass(e.target.value)}
                      className="bg-slate-900 border-slate-700 text-white text-xs"
                    />
                  </div>
                </div>
              </div>

              <div className="p-4 bg-slate-800/60 border border-slate-700 rounded-xl">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Zap className="h-4 w-4 text-emerald-400" />
                    <span className="text-xs font-bold text-white">Customer-Owned WhatsApp API</span>
                  </div>
                  <Switch checked={enableWhatsApp} onCheckedChange={setEnableWhatsApp} />
                </div>
                <p className="text-[11px] text-slate-400 mb-3">
                  Direct Cloud API integration without per-message internal credits or artificial markups.
                </p>

                {enableWhatsApp && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                    <div>
                      <label className="block text-xs font-semibold text-slate-300 mb-1">Phone Number ID</label>
                      <Input
                        value={waPhoneId}
                        onChange={(e) => setWaPhoneId(e.target.value)}
                        placeholder="e.g. 1048291048"
                        className="bg-slate-900 border-slate-700 text-white text-xs font-mono"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-300 mb-1">Permanent Access Token</label>
                      <Input
                        type="password"
                        value={waToken}
                        onChange={(e) => setWaToken(e.target.value)}
                        placeholder="EAAB..."
                        className="bg-slate-900 border-slate-700 text-white text-xs font-mono"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* STEP 5: AI & AUTOMATIONS */}
          {currentStep === 5 && (
            <div className="space-y-4">
              <div className="p-4 bg-slate-800/60 border border-slate-700 rounded-xl">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Cpu className="h-4 w-4 text-amber-400" />
                    <span className="text-xs font-bold text-white">Local Privacy AI (Ollama)</span>
                  </div>
                  <Switch checked={enableOllama} onCheckedChange={setEnableOllama} />
                </div>
                <p className="text-[11px] text-slate-400 mb-2">
                  Run local AI inference (Llama 3.2) directly on desktop. Zero data leaves your computer.
                </p>
                {enableOllama && (
                  <div className="text-[11px] text-slate-400 flex items-center gap-2">
                    <span>Target endpoint: <code className="text-amber-400 font-mono">http://localhost:11434</code></span>
                    <Badge variant="outline" className="text-[10px] border-slate-700 text-slate-300">Model: {ollamaModel}</Badge>
                  </div>
                )}
              </div>

              <div className="p-4 bg-slate-800/60 border border-slate-700 rounded-xl space-y-3">
                <h3 className="text-xs font-bold text-white flex items-center gap-2">
                  <Zap className="h-4 w-4 text-amber-500" />
                  Scheduled Automations & Alerts
                </h3>

                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs font-medium text-slate-200">Scheduled Daily Closure</div>
                    <div className="text-[11px] text-slate-400">Snapshot financial and gold position automatically</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      type="time"
                      value={dailyCloseTime}
                      onChange={(e) => setDailyCloseTime(e.target.value)}
                      className="bg-slate-900 border-slate-700 text-white text-xs w-28 h-8 font-mono"
                    />
                    <Switch checked={enableDailyClose} onCheckedChange={setEnableDailyClose} />
                  </div>
                </div>

                <div className="flex items-center justify-between border-t border-slate-700/50 pt-3">
                  <div>
                    <div className="text-xs font-medium text-slate-200">Automated Owner Email Digest</div>
                    <div className="text-[11px] text-slate-400">Email daily financial & gold report to {ownerEmail}</div>
                  </div>
                  <Switch checked={enableOwnerDigest} onCheckedChange={setEnableOwnerDigest} />
                </div>
              </div>
            </div>
          )}

          {/* STEP 6: MASTER PASSWORD & CONFIGURATION LOCK */}
          {currentStep === 6 && (
            <div className="space-y-5">
              <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-xl">
                <h3 className="text-xs font-bold text-amber-400 mb-1">Configuration Lock State (LOCKED_CONFIGURATION)</h3>
                <p className="text-[11px] text-slate-300 leading-relaxed">
                  Upon completion, technical setup controls will be finalized and locked to ensure a clean, uncluttered daily interface. Modifying business rules later requires Admin Password + verified Owner OTP.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1.5">Master Company Password</label>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={custPassword}
                      onChange={(e) => setCustPassword(e.target.value)}
                      placeholder="••••••••"
                      className="bg-slate-800/80 border-slate-700 text-white text-sm pr-9"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-2.5 top-2.5 text-slate-400 hover:text-white"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  <span className="text-[10px] text-slate-500">Derives AES-256-GCM encryption key</span>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1.5">Confirm Master Password</label>
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={custConfirm}
                    onChange={(e) => setCustConfirm(e.target.value)}
                    placeholder="••••••••"
                    className="bg-slate-800/80 border-slate-700 text-white text-sm"
                  />
                  <span className="text-[10px] text-slate-500">Must match password above</span>
                </div>
              </div>

              {/* Setup Summary Checklist */}
              <div className="p-4 bg-slate-800/60 border border-slate-700 rounded-xl">
                <div className="text-xs font-bold text-white mb-2">Final Verification Summary:</div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-[11px] text-slate-300">
                  <div className="flex items-center gap-1.5 text-emerald-400">
                    <Check className="h-3.5 w-3.5" />
                    <span>Owner Email Verified</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-emerald-400">
                    <Check className="h-3.5 w-3.5" />
                    <span>995 Fine Benchmark</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-emerald-400">
                    <Check className="h-3.5 w-3.5" />
                    <span>GST 3% Tax Engine</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-emerald-400">
                    <Check className="h-3.5 w-3.5" />
                    <span>14 Jelly Modules</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-emerald-400">
                    <Check className="h-3.5 w-3.5" />
                    <span>Hostinger SMTP Ready</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-emerald-400">
                    <Check className="h-3.5 w-3.5" />
                    <span>AES-256-GCM Encrypted</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Bottom Navigation Controls */}
          <div className="flex items-center justify-between border-t border-slate-800 pt-4 mt-6">
            <Button
              variant="outline"
              size="sm"
              disabled={currentStep === 0 || isProcessing}
              onClick={() => setCurrentStep((prev) => Math.max(0, prev - 1))}
              className="border-slate-700 text-slate-300 hover:bg-slate-800 text-xs"
            >
              <ArrowLeft className="h-3.5 w-3.5 mr-1.5" /> Back
            </Button>

            {currentStep < 6 ? (
              <Button
                size="sm"
                disabled={currentStep === 0 && !isEmailVerified}
                onClick={() => setCurrentStep((prev) => Math.min(6, prev + 1))}
                className="bg-amber-600 hover:bg-amber-500 text-white font-semibold text-xs px-5"
              >
                Next Step <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
              </Button>
            ) : (
              <Button
                size="sm"
                disabled={isProcessing}
                onClick={handleFinishCustomSetup}
                className="bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs px-6 shadow-lg shadow-emerald-600/20"
              >
                {isProcessing ? "Configuring ERP..." : "Create Company & Lock Configuration"}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
